import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from "../context/AuthContext";

import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  Snackbar, Alert as MuiAlert
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import LanguageSwitcher from '../components/LanguageSwitcher';

const GradientCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[2],
  border: `1px solid ${theme.palette.divider}`,
  maxWidth: 500,
  margin: '0 auto'
}));


const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshAuth } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ open: false, type: '', text: '' });
  const [showPassword, setShowPassword] = useState({
    new: false,
    confirm: false
  });
  const [validToken, setValidToken] = useState(false);
  const [token, setToken] = useState('');

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});


  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get('token');

    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      verifyToken(tokenFromUrl);
    } else {
      setLoading(false);
      setMessage({
        open: true,
        type: 'error',
        text: t('resetPassword.invalidToken')
      });
      setTimeout(() => navigate('/'), 3000);
    }
  }, [location, navigate, t]);


  const verifyToken = async (token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/verify-reset-token`,
        { token },
        { withCredentials: true }
      );

      if (response.data.valid) {
        setValidToken(true);
      } else {
        setMessage({
          open: true,
          type: 'error',
          text: response.data.error || t('resetPassword.invalidToken')
        });
      }
    } catch (err) {
      setMessage({
        open: true,
        type: 'error',
        text: err.response?.data?.error || t('resetPassword.tokenError')
      });
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ open: true, type, text });
  };

  const handleCloseMessage = () => {
    setMessage({ ...message, open: false });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleClickShowPassword = (field) => {
    setShowPassword({ ...showPassword, [field]: !showPassword[field] });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.newPassword) {
      newErrors.newPassword = t('resetPassword.passwordRequired');
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = t('resetPassword.passwordTooShort');
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('resetPassword.confirmPasswordRequired');
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t('resetPassword.passwordMismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/reset-password`,
        {
          token,
          newPassword: formData.newPassword
        },
        { withCredentials: true }
      );

      showMessage('success', response.data.message || t('resetPassword.success'));
      refreshAuth() //! esto es critico!, debemos pasar SI O SI, se encarga del reload para navegar en la ruta que deberia ser si hay token!

      setTimeout(() => {
        navigate('/');
      }, 3000);

    } catch (err) {
      showMessage('error', err.response?.data?.error || t('resetPassword.error'));
    } finally {

    }
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
          {t('resetPassword.verifyingToken')}
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" fontWeight="bold" color="primary" gutterBottom>
          {t('resetPassword.title')}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {t('resetPassword.subtitle')}
        </Typography>
      </Box>

      <GradientCard>
        <CardContent sx={{ p: 4 }}>
          {/* {message.open && (
            <Alert 
              severity={message.type} 
              onClose={handleCloseMessage}
              sx={{ mb: 3 }}
            >
              {message.text}
            </Alert>
          )} */}
          {submitting && (
            <Snackbar
              open={submitting}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              sx={{ zIndex: 9999 }}
            >
              <MuiAlert
                severity="info"
                icon={<CircularProgress size={18} />}
                sx={{ width: '100%' }}
              >
                {t('resetPassword.processing')}
              </MuiAlert>
            </Snackbar>
          )}
          <LanguageSwitcher />
          {!validToken ? (
            <Box textAlign="center">
              <Typography variant="body1" color="error" gutterBottom>
                {t('resetPassword.invalidTokenMessage')}
              </Typography>
              <Button
                variant="contained"
                component={Link}
                to="/forgot-password"
                sx={{ mt: 2 }}
              >
                {t('resetPassword.requestNewLink')}
              </Button>
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              <Typography variant="h5" fontWeight="bold" gutterBottom color="primary">
                {t('resetPassword.enterNewPassword')}
              </Typography>

              <Typography variant="body2" color="text.secondary" paragraph>
                {t('resetPassword.instructions')}
              </Typography>

              <TextField
                fullWidth
                label={t('resetPassword.newPassword')}
                name="newPassword"
                type={showPassword.new ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={handleChange}
                required
                error={!!errors.newPassword}
                helperText={errors.newPassword}
                margin="normal"
                InputProps={{
                  startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleClickShowPassword('new')}
                        edge="end"
                      >
                        {showPassword.new ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                fullWidth
                label={t('resetPassword.confirmPassword')}
                name="confirmPassword"
                type={showPassword.confirm ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                margin="normal"
                InputProps={{
                  startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleClickShowPassword('confirm')}
                        edge="end"
                      >
                        {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <Box sx={{ mt: 4 }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  type="submit"
                  size="large"
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                  sx={{ py: 1.5, borderRadius: 2 }}
                >
                  {submitting ? t('resetPassword.resetting') : t('resetPassword.resetButton')}
                </Button>
              </Box>

              <Box textAlign="center" mt={3}>
                <Button
                  component={Link}
                  to="/"
                  color="primary"
                >
                  {t('resetPassword.backToLogin')}
                </Button>
              </Box>
            </form>
          )}
        </CardContent>
      </GradientCard>

      <Box textAlign="center" mt={4}>
        <Typography variant="body2" color="text.secondary">
          {t('resetPassword.securityNote')}
        </Typography>
      </Box>
    </Container>
  );
};

export default ResetPassword; 